import React, { useEffect, useState } from "react";
import { Plus, Search, AlertOctagon, Check } from "lucide-react";
import api from "../lib/api";
import { DashboardShell } from "../components/shared/DashboardShell";
import { StatusBadge } from "../components/shared/StatusBadge";
import { studentNav, companyNav, adminNav } from "../components/shared/navConfig";
import { useAuth } from "../context/AppContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";

const TYPES = [
  { k: "payment",       l: "Payment" },
  { k: "work_quality",  l: "Work quality" },
  { k: "scope",         l: "Scope" },
  { k: "deadline",      l: "Deadline" },
  { k: "cancellation",  l: "Cancellation" },
  { k: "misconduct",    l: "Misconduct" },
];
const RESOLUTIONS = [
  { k: "resolve",             l: "Resolve" },
  { k: "refund",              l: "Refund" },
  { k: "rework",              l: "Rework" },
  { k: "partial_settlement",  l: "Partial settlement" },
  { k: "cancel",              l: "Cancel" },
];
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function Disputes({ adminView = false }) {
  const { role } = useAuth();
  const nav = adminView ? adminNav : role === "company" ? companyNav : studentNav;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openResolve, setOpenResolve] = useState(null); // dispute obj
  const [form, setForm] = useState({ task_id: "", dispute_type: "payment", subject: "", description: "" });
  const [rf, setRf] = useState({ resolution: "resolve", note: "", settlement_amount: "" });

  const load = async () => {
    setLoading(true);
    try {
      const url = adminView ? "/admin/disputes" : "/disputes/mine";
      const { data } = await api.get(url);
      setRows(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [adminView]); // eslint-disable-line

  const loadTasks = async () => {
    try {
      const url = role === "company" ? "/tasks/mine" : "/workrooms/mine";
      const { data } = await api.get(url);
      if (role === "company") setTasks(data);
      else setTasks(data.map(w => w.task).filter(Boolean));
    } catch {}
  };
  useEffect(() => { if (!adminView) loadTasks(); }, [adminView, role]); // eslint-disable-line

  const submit = async () => {
    if (!form.task_id || !form.subject || !form.description) { toast.error("Fill required fields"); return; }
    try {
      await api.post("/disputes", form);
      toast.success("Dispute filed"); setOpenNew(false);
      setForm({ task_id: "", dispute_type: "payment", subject: "", description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resolve = async () => {
    if (!openResolve) return;
    try {
      await api.post(`/admin/disputes/${openResolve.dispute_id}/resolve`, {
        resolution: rf.resolution, note: rf.note || null,
        settlement_amount: rf.settlement_amount ? Number(rf.settlement_amount) : null,
      });
      toast.success("Dispute resolved");
      setOpenResolve(null); setRf({ resolution: "resolve", note: "", settlement_amount: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter(r => !q || (r.subject || "").toLowerCase().includes(q.toLowerCase()) || (r.task_title || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardShell nav={nav} title={adminView ? "Dispute center" : "Disputes"} subtitle={adminView ? "Review and resolve reported disputes" : "Raise or track disputes on your tasks"}
      headerRight={!adminView && (
        <button data-testid="new-dispute-btn" onClick={() => setOpenNew(true)} className="btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5"><Plus size={13}/> File dispute</button>
      )}>
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border flex-1 max-w-md" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
            <Search size={13} className="text-zinc-500"/>
            <input data-testid="disputes-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 flex-1"/>
          </div>
          <div className="text-xs text-zinc-500">{filtered.length} record{filtered.length !== 1 && "s"}</div>
        </div>

        {loading ? <div className="py-12 text-center text-sm text-zinc-500">Loading…</div> :
          filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              <AlertOctagon size={22} className="mx-auto mb-3 text-zinc-600"/>No disputes.
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(d => {
                const status = d.status === "Resolved" ? "Completed" : d.status === "Open" ? "Pending" : d.status;
                return (
                  <div key={d.dispute_id} data-testid={`dispute-row-${d.dispute_id}`} className="p-4 rounded-md border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[10px] font-mono text-zinc-500">{d.dispute_id}</span>
                      <span className="chip" style={{ background: "rgba(148,163,184,0.10)", color: "#94A3B8", borderColor: "rgba(148,163,184,0.35)" }}>
                        {(d.dispute_type || "").replace("_", " ")}
                      </span>
                      <StatusBadge status={status}/>
                      <span className="ml-auto text-[10px] font-mono text-zinc-500">{fmt(d.created_at)}</span>
                    </div>
                    <div className="text-sm text-white mt-2">{d.subject}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Task: {d.task_title} · Filed by {d.raised_by_name} ({d.raised_by_role})</div>
                    <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{d.description}</div>
                    {d.resolution && (
                      <div className="mt-3 p-2 rounded-md text-[11px]" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)" }}>
                        <span className="text-emerald-400">Resolution: {d.resolution.replace("_", " ")}</span>
                        {d.resolver_note && <span className="text-zinc-400"> — {d.resolver_note}</span>}
                      </div>
                    )}
                    {adminView && d.status === "Open" && (
                      <div className="mt-3 flex justify-end">
                        <button data-testid={`resolve-btn-${d.dispute_id}`} onClick={() => setOpenResolve(d)} className="btn-primary px-3 py-1.5 rounded-full text-xs">Resolve</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* File dispute */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "var(--text)" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">File a dispute</DialogTitle>
            <DialogDescription className="text-zinc-500">Admin will review and get back to you.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Task</span>
              <select data-testid="dispute-task-select" value={form.task_id} onChange={e => setForm(x => ({ ...x, task_id: e.target.value }))} className={ic + " appearance-none mt-1.5"}>
                <option value="">Select task…</option>
                {tasks.map(t => <option key={t.task_id} value={t.task_id}>{t.title} · {t.task_id}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Type</span>
              <select data-testid="dispute-type-select" value={form.dispute_type} onChange={e => setForm(x => ({ ...x, dispute_type: e.target.value }))} className={ic + " appearance-none mt-1.5"}>
                {TYPES.map(t => <option key={t.k} value={t.k}>{t.l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Subject</span>
              <input data-testid="dispute-subject" value={form.subject} onChange={e => setForm(x => ({ ...x, subject: e.target.value }))} placeholder="Brief summary" className={ic + " mt-1.5"}/>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Description</span>
              <textarea data-testid="dispute-description" rows={4} value={form.description} onChange={e => setForm(x => ({ ...x, description: e.target.value }))} placeholder="What happened? Include dates and evidence." className={ic + " resize-none mt-1.5"}/>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpenNew(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button data-testid="dispute-submit-btn" onClick={submit} className="btn-primary px-4 py-2 rounded-md text-sm">File dispute</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin resolve */}
      <Dialog open={!!openResolve} onOpenChange={() => setOpenResolve(null)}>
        <DialogContent className="max-w-md" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "var(--text)" }}>
          {openResolve && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Resolve dispute</DialogTitle>
                <DialogDescription className="text-zinc-500">{openResolve.subject} · <span className="font-mono">{openResolve.dispute_id}</span></DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Resolution</span>
                  <select data-testid="dispute-resolution-select" value={rf.resolution} onChange={e => setRf(x => ({ ...x, resolution: e.target.value }))} className={ic + " appearance-none mt-1.5"}>
                    {RESOLUTIONS.map(r => <option key={r.k} value={r.k}>{r.l}</option>)}
                  </select>
                </label>
                {rf.resolution === "partial_settlement" && (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Settlement amount (₹)</span>
                    <input data-testid="dispute-settlement-amount" type="number" value={rf.settlement_amount} onChange={e => setRf(x => ({ ...x, settlement_amount: e.target.value }))} className={ic + " mt-1.5"}/>
                  </label>
                )}
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Note to parties</span>
                  <textarea data-testid="dispute-resolve-note" rows={3} value={rf.note} onChange={e => setRf(x => ({ ...x, note: e.target.value }))} className={ic + " resize-none mt-1.5"}/>
                </label>
              </div>
              <DialogFooter>
                <button onClick={() => setOpenResolve(null)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
                <button data-testid="dispute-resolve-confirm" onClick={resolve} className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2">
                  <Check size={13}/> Confirm
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

const ic = "w-full px-3 py-2.5 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-blue-500/50 border-[#27272A]";
